// D-pad (arrow key) navigation for TV remotes – Fire TV, Android TV, Tizen, webOS – where
// there is no Tab key or mouse. Moves focus to the nearest focusable element in the
// pressed direction, the way native TV apps behave.

export type Direction = 'up' | 'down' | 'left' | 'right';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const KEY_DIRECTION: Record<string, Direction> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

function isVisible(el: HTMLElement): boolean {
  if (el.closest('[aria-hidden="true"], [inert]')) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
}

export function focusables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible);
}

/**
 * Score candidates by distance along the direction plus a heavier penalty for drifting
 * sideways, so "right" from a menu item picks the control level with it, not one far below.
 * Up/Down stay within the current column (candidates must overlap horizontally), so the
 * top of the menu doesn't jump across to, say, a close button in the corner.
 */
export function pickNext(from: DOMRect, candidates: Array<{ el: HTMLElement; rect: DOMRect }>, dir: Direction) {
  if (dir === 'up' || dir === 'down') {
    candidates = candidates.filter(({ rect }) => rect.left < from.right - 1 && rect.right > from.left + 1);
  }
  const cx = from.left + from.width / 2;
  const cy = from.top + from.height / 2;
  let best: { el: HTMLElement; score: number } | null = null;
  for (const { el, rect } of candidates) {
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    let along: number;
    let across: number;
    switch (dir) {
      case 'right':
        if (rect.left < from.right - 4 && x <= cx + 1) continue;
        along = Math.max(0, rect.left - from.right);
        across = rect.bottom < from.top ? from.top - rect.bottom : rect.top > from.bottom ? rect.top - from.bottom : 0;
        break;
      case 'left':
        if (rect.right > from.left + 4 && x >= cx - 1) continue;
        along = Math.max(0, from.left - rect.right);
        across = rect.bottom < from.top ? from.top - rect.bottom : rect.top > from.bottom ? rect.top - from.bottom : 0;
        break;
      case 'down':
        if (rect.top < from.bottom - 4 && y <= cy + 1) continue;
        along = Math.max(0, rect.top - from.bottom);
        across = rect.right < from.left ? from.left - rect.right : rect.left > from.right ? rect.left - from.right : 0;
        break;
      default: // up
        if (rect.bottom > from.top + 4 && y >= cy - 1) continue;
        along = Math.max(0, from.top - rect.bottom);
        across = rect.right < from.left ? from.left - rect.right : rect.left > from.right ? rect.left - from.right : 0;
    }
    const score = along + across * 3;
    if (!best || score < best.score) best = { el, score };
  }
  return best?.el ?? null;
}

/** Moves focus within `container`; returns true if focus moved (so the key can be swallowed). */
export function moveFocus(container: HTMLElement, dir: Direction): boolean {
  const all = focusables(container);
  if (!all.length) return false;
  const active = document.activeElement as HTMLElement | null;
  if (!active || !container.contains(active) || active === container) {
    const start = all.find((el) => el.getAttribute('aria-selected') === 'true') ?? all[0]!;
    start.focus();
    return true;
  }
  const from = active.getBoundingClientRect();
  const next = pickNext(
    from,
    all.filter((el) => el !== active && !el.contains(active)).map((el) => ({ el, rect: el.getBoundingClientRect() })),
    dir,
  );
  if (!next) return false;
  delete active.dataset.adjusting; // leaving a slider ends its adjust mode
  next.focus();
  next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  return true;
}

/**
 * Keydown handler for a container. Text fields keep Left/Right for the caret until it
 * reaches an end; sliders take Left/Right only after OK puts them in adjust mode (OK again
 * to finish); Up/Down always navigate. OK toggles checkboxes (remotes have no Space key).
 */
export function handleSpatialKey(e: KeyboardEvent, container: HTMLElement): void {
  const target = e.target as HTMLElement | null;
  if (e.key === 'Enter' && target instanceof HTMLInputElement && (target.type === 'checkbox' || target.type === 'radio')) {
    e.preventDefault();
    target.click();
    return;
  }
  // Sliders: OK toggles "adjust" mode, in which Left/Right change the value.
  if (e.key === 'Enter' && target instanceof HTMLInputElement && target.type === 'range') {
    e.preventDefault();
    if (target.dataset.adjusting) delete target.dataset.adjusting;
    else target.dataset.adjusting = '1';
    return;
  }
  const dir = KEY_DIRECTION[e.key];
  if (!dir || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
  const horizontal = dir === 'left' || dir === 'right';
  if (horizontal && target instanceof HTMLInputElement) {
    // Sliders and text fields use Left/Right themselves – until they hit their end, then
    // the key moves on, so a remote can never get stuck in one.
    if (target.type === 'range') {
      if (target.dataset.adjusting) return;
    } else if (['text', 'search', 'email', 'password', 'url', 'number'].includes(target.type)) {
      const pos = dir === 'left' ? target.selectionStart : target.selectionEnd;
      const edge = dir === 'left' ? 0 : target.value.length;
      if (pos === null || pos !== edge || target.selectionStart !== target.selectionEnd) return;
    }
  }
  if (horizontal && target instanceof HTMLTextAreaElement) return;
  if (target instanceof HTMLTextAreaElement && !horizontal) {
    // Let the caret move between lines; leave the box only from its first/last line.
    const atStart = target.selectionStart === 0;
    const atEnd = target.selectionEnd === target.value.length;
    if ((dir === 'up' && !atStart) || (dir === 'down' && !atEnd)) return;
  }
  if (moveFocus(container, dir)) e.preventDefault();
}
